import type { ReactNode } from 'react'
import { Bar, BarChart, CartesianGrid, Rectangle, Tooltip } from 'recharts'
import {
  PLOT_TOP,
  PREV_BAR,
  PREV_SWATCH,
  PlotContainer,
  PeriodTooltip,
  currentFirst,
  gridProps,
  xAxis,
  yAxis,
  yScale,
  type PeriodPoint,
} from './chartCardParts'

/** Hover band: rgb(0 0 0 / 3%), 6px radius, 80% of the day slot */
function BandCursor(props: { x?: number; y?: number; width?: number; height?: number }) {
  const { x = 0, y = 0, width = 0, height = 0 } = props
  if (!Number.isFinite(x) || !Number.isFinite(width) || width <= 0) return null
  const w = width * 0.8
  return <Rectangle x={x + (width - w) / 2} y={y} width={w} height={height} radius={6} fill="rgb(0 0 0 / 3%)" />
}

/**
 * Money over time (Paper "Charts · Card pattern", bar card): money is bars,
 * never a line. Each slot holds a 16px mist-200 ghost bar for the previous
 * period on the left and the current bar in `color` on the right, 2px apart,
 * 4px top radius, square at zero. Hover lights the slot with a 3% band.
 *
 * `showPrevious={false}` (or no previous values) drops the ghost bars.
 */
export function PeriodBarChart({
  data,
  labels,
  color = 'var(--chart-revenue)',
  showPrevious = true,
  formatValue,
  formatAxis,
  overlay,
}: {
  data: PeriodPoint[]
  labels: { current: string; previous: string }
  color?: string
  showPrevious?: boolean
  formatValue: (n: number) => string
  formatAxis: (n: number) => string
  overlay?: ReactNode
}) {
  const withPrevious = showPrevious && data.some((p) => p.previous != null)
  const { ticks, domain } = yScale(data, withPrevious)
  const config = {
    current: { label: labels.current, color },
    previous: { label: labels.previous, color: PREV_SWATCH },
  }

  return (
    <div className="relative">
      <PlotContainer config={config}>
        <BarChart data={data} margin={{ top: PLOT_TOP, right: 0, bottom: 0, left: 0 }} barGap={2} barCategoryGap="20%">
          <CartesianGrid {...gridProps} />
          {xAxis(data.length)}
          {yAxis(ticks, domain, formatAxis)}
          {!overlay && (
            <Tooltip
              cursor={<BandCursor />}
              offset={30}
              isAnimationActive={false}
              itemSorter={currentFirst}
              content={
                <PeriodTooltip
                  labels={labels}
                  colors={{ current: color, previous: PREV_SWATCH }}
                  format={formatValue}
                />
              }
            />
          )}
          {withPrevious && (
            <Bar dataKey="previous" name="previous" fill={PREV_BAR} barSize={16} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          )}
          {!overlay && (
            <Bar dataKey="current" name="current" fill={color} barSize={16} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          )}
        </BarChart>
      </PlotContainer>
      {overlay}
    </div>
  )
}

export default PeriodBarChart
